import React from 'react'
import { useRouter } from 'next/router'
import Link from 'next/link'
import Layout from 'library/atoms/Layout'
import Header from 'library/molecules/Header'
import Head from 'next/head'
import { gql, useMutation } from '@apollo/client'
import { Basket_Response } from 'shared/presenters/graphqlTypes'
import { H4, Text } from 'library/atoms/Typography'
import BottomBar from 'library/atoms/BottomBar'
import SchedulableAction from 'library/molecules/SchedulableAction'
import Button from 'library/atoms/Button'
import widgetRoutes from 'enums/routes'
import useTranslation from 'next-translate/useTranslation'
import Spinner from 'library/atoms/Spinner'
import { LineItemPresenter, OutletPresenter } from 'shared/presenters'
import LineItem from 'library/molecules/LineItem'
import BasketPrice from 'library/molecules/BasketPrice'
import {
  LineItemInterface,
  LineItemList
} from 'library/molecules/LineItem/LineItem'
import useBasketTotalPrice from 'state/useBasketTotalPrice'
import useBasket from 'state/useBasket'
import { SetActiveModalId } from 'shared/library/molecules/Modal'
import { MODAL_IDS } from 'library/molecules/Header/HeaderModals'
import EnthusiastFee from 'library/molecules/EnthusiastFee'
import { BottomBarContainer } from 'library/atoms/BottomBar/BottomBar'
import Breadcrumb from 'library/molecules/Breadcrumb'
import { defined } from 'shared/presenters/presenter'

export interface BasketProps
  extends Pick<LineItemInterface, 'completeActivityVoucherBooking'> {
  outlet: OutletPresenter
  view?: string
  setActiveModalId?: SetActiveModalId<MODAL_IDS>
}

const Basket = ({ outlet, completeActivityVoucherBooking }: BasketProps) => {
  const { t } = useTranslation('basket')
  const { query } = useRouter()
  const widgetSlug = query.widgetSlug as string
  const { basket, basketLoading, refetchBasket } = useBasket()

  const {
    basketTotalPrice,
    enthusiastFeePence,
    basketTotalPriceLoading,
    basketTotalPricePreAdjustments,
    basketTotalPriceError
  } = useBasketTotalPrice()

  const lineItems = basket?.line_items?.map(item => new LineItemPresenter(item))

  const [removeBasketItem, { loading: removeBasketItemLoading }] = useMutation<{
    Consumer_Baskets_RemoveItem: Basket_Response
  }>(REMOVE_BASKET_ITEM, {
    onCompleted({ Consumer_Baskets_RemoveItem }) {
      refetchBasket({ basketId: Consumer_Baskets_RemoveItem.basket_id })
    }
  })

  const lineItemsCount = lineItems?.length

  const removeBasketItemOnClick = async (itemId: string) => {
    if (!basket?.id) {
      return
    }

    try {
      await removeBasketItem({
        variables: {
          lineItemId: itemId
        }
      })
    } catch (err) {
      // catching here to protect against page breaks
    }
  }

  return (
    <>
      <Head>
        <title>{t('basket')}</title>
      </Head>
      <Layout>
        {/**
         *
         * Header
         *
         */}
        <Header />
        <Breadcrumb
          pages={[
            {
              link: widgetRoutes.HOME.url({ widgetSlug }),
              name: defined(outlet.name),
              image: outlet.logoImageURL()
            },
            {
              link: '',
              name: 'Basket',
              currentPage: true
            }
          ]}
          firstLinkAsHome={true}
        />
        {/**
         *
         * Main
         *
         */}
        <main>
          {basketLoading ? (
            <Spinner centered />
          ) : basketTotalPriceError ? (
            <>
              <Text>{t('basket-error')}</Text>
              <Text paragraphMargins>
                <Link href={widgetRoutes.HOME.url({ widgetSlug })}>
                  {t('common:return-to-home')}
                </Link>
              </Text>
            </>
          ) : !lineItemsCount ? (
            <Text>{t('0-basket-items')}</Text>
          ) : (
            lineItems && (
              <LineItemList variant="contained">
                <H4>{t('basket:my-basket')}</H4>
                {lineItems.map(item => (
                  <LineItem
                    key={item.id}
                    item={item}
                    outlet={outlet}
                    removeItemLoading={removeBasketItemLoading || basketLoading}
                    removeItemOnClick={removeBasketItemOnClick}
                    completeActivityVoucherBooking={
                      completeActivityVoucherBooking
                    }
                    expand={true}
                  />
                ))}
                <EnthusiastFee
                  pence={enthusiastFeePence}
                  currency={outlet.currency()}
                />
              </LineItemList>
            )
          )}
        </main>
        {/**
         *
         * BottomBar
         *
         */}
        <BottomBar padding={0}>
          <BottomBarContainer>
            <SchedulableAction
              padding={0}
              border={0}
              actions={
                <Button
                  as="a"
                  inactive={!lineItemsCount || basket?.hasIncompleteBooking()}
                  href={widgetRoutes.CHECKOUT.url({ widgetSlug })}
                >
                  {t('common:checkout')}
                </Button>
              }
            >
              <Text color="tertiary" fontStyle="h5">
                {t('checkout:total')}
              </Text>
              {!basket?.hasIncompleteBooking() && (
                <BasketPrice
                  basketId={basket?.id}
                  preAdjustmentsPence={basketTotalPricePreAdjustments}
                  pence={basketTotalPrice}
                  priceLoading={basketTotalPriceLoading}
                  currency={outlet.currency()}
                />
              )}
            </SchedulableAction>
          </BottomBarContainer>
        </BottomBar>
      </Layout>
    </>
  )
}

export default Basket

export const REMOVE_BASKET_ITEM = gql`
  mutation WidgetBasketRemoveItem($lineItemId: ID!) {
    Consumer_Baskets_RemoveItem(line_item_id: $lineItemId) {
      basket_id
    }
  }
`
